import React, { useEffect, useState } from 'react';
import { generateTableOfContents } from './generateTableOfContents';
import { CollapsibleBlock } from '../../../components/molecules/CollapsibleBlock';
import { LoadingSpinner } from '../../../components/atoms/LoadingSpinner';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { SkipLink, SkipLinkTarget } from '../../../_lib/components/SkipLink';
import { ContentParagraph } from '../../../_lib/components/ContentParagraph';

export type TableOfContents = { editModeActive: boolean; visible: boolean; uuid: string };

const TOCDefaultSettings = {
  levels: [
    'h2:not(.invisible)',
    'h3:not(.descripted-definition-list__headline, .content-headline, .invisible)',
    'h4:not(.invisible)',
    'h5:not(.invisible)',
    'h6:not(.invisible)',
  ].join(','),
  headingLevel: 'h2',
  listType: 'ol',
};

export function TableOfContents({ editModeActive, uuid, visible }: TableOfContents) {
  const { t } = useSonTranslation('TableOfContents');
  const [containsHeadings, setContainsHeadings] = useState(false);

  // we check if there are any headings
  useEffect(() => {
    const contentWrapperElements = document.getElementsByClassName('content-wrapper');
    const headingElements = contentWrapperElements[0]?.querySelectorAll(TOCDefaultSettings.levels);

    // if there are headings in content-wrapper, we generate a TOC
    if (headingElements?.length > 0) {
      setContainsHeadings(true);
    }
  }, []);

  // if we have headings and the component was mounted, we let the magic of the script take place.
  useEffect(() => {
    if (containsHeadings && !editModeActive) {
      generateTableOfContents(uuid, '.content-wrapper', '#table-of-contents', TOCDefaultSettings);
    }
  }, [containsHeadings, editModeActive, uuid]);

  if (!containsHeadings || (!editModeActive && !visible)) {
    return null;
  }

  /**
   * different default view in edit mode to inform user of activation
   * no information if disabled
   * */
  if (editModeActive) {
    return (
      <div className="table-of-contents">
        <ContentParagraph>{visible ? t('default.enabled') : t('default.disabled')}</ContentParagraph>
      </div>
    );
  }

  return (
    <CollapsibleBlock id={uuid} title={t('default.headline')} closed>
      <div className="table-of-contents" aria-labelledby={`table-of-contents-${uuid}`}>
        <div className="table-of-contents__skip-links">
          <SkipLinkTarget targetName={t('default.skipLinkTargetTop')} id="table-of-contents-top" />
          <SkipLink id="table-of-contents-bottom">{t('default.skipLinkNameTop')}</SkipLink>
        </div>
        <div id="table-of-contents">
          {/* As fallback, if the script take very long time. this spinner will be destroyed by the script. */}
          <div className="table-of-contents__loading-spinner">
            <LoadingSpinner />
          </div>
        </div>
        <div className="table-of-contents__skip-links">
          <SkipLinkTarget targetName={t('default.skipLinkTargetBottom')} id="table-of-contents-bottom" />
          <SkipLink id="table-of-contents-top">{t('default.skipLinkNameBottom')}</SkipLink>
        </div>
      </div>
    </CollapsibleBlock>
  );
}
